/**
 * A minimal ZIP writer: every entry is stored uncompressed, which is all an `.xlsx` package needs.
 */
const ZIP_VERSION = 20;
const NAME_ENCODER = new TextEncoder();

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

const CRC32_LOOKUP = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < table.length; i++) {
    let crc = i;
    for (let bit = 0; bit < 8; bit++) {
      const shouldXor = (crc & 1) !== 0;
      crc = crc >>> 1;
      if (shouldXor) {
        crc ^= 0xedb88320;
      }
    }
    table[i] = crc >>> 0;
  }
  return table;
})();

export function createZip(entries: ZipEntry[]): Uint8Array {
  const localFileParts: Uint8Array[] = [];
  const centralDirectoryParts: Uint8Array[] = [];
  let fileOffset = 0;

  for (const entry of entries) {
    const fileNameBytes = NAME_ENCODER.encode(entry.name);
    const data = entry.data;
    const crc = crc32(data);

    const localHeader = new Uint8Array(30 + fileNameBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, ZIP_VERSION, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, data.length, true);
    localView.setUint32(22, data.length, true);
    localView.setUint16(26, fileNameBytes.length, true);
    localView.setUint16(28, 0, true);
    localHeader.set(fileNameBytes, 30);

    const centralHeader = new Uint8Array(46 + fileNameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, ZIP_VERSION, true);
    centralView.setUint16(6, ZIP_VERSION, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, fileNameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, fileOffset, true);
    centralHeader.set(fileNameBytes, 46);

    localFileParts.push(localHeader, data);
    centralDirectoryParts.push(centralHeader);

    fileOffset += localHeader.length + data.length;
  }

  const centralDirectoryOffset = fileOffset;
  const centralDirectory = concatUint8Arrays(centralDirectoryParts);
  const endOfCentralDirectory = new Uint8Array(22);
  const eocdView = new DataView(endOfCentralDirectory.buffer);
  eocdView.setUint32(0, 0x06054b50, true);
  eocdView.setUint16(4, 0, true);
  eocdView.setUint16(6, 0, true);
  eocdView.setUint16(8, entries.length, true);
  eocdView.setUint16(10, entries.length, true);
  eocdView.setUint32(12, centralDirectory.length, true);
  eocdView.setUint32(16, centralDirectoryOffset, true);
  eocdView.setUint16(20, 0, true);

  return concatUint8Arrays([...localFileParts, centralDirectory, endOfCentralDirectory]);
}

export function concatUint8Arrays(arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, array) => sum + array.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  for (const array of arrays) {
    merged.set(array, offset);
    offset += array.length;
  }

  return merged;
}

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const value of data) {
    const index = (crc ^ value) & 0xff;
    crc = (crc >>> 8) ^ CRC32_LOOKUP[index];
  }
  return (crc ^ 0xffffffff) >>> 0;
}
